/**
 * SKU search via local index (all catalog SKUs from /collections/all/products.json).
 * Supports very short fragments (e.g. "65", "76") by substring match on variant SKU.
 */
(function () {
  const MIN_DIGIT_QUERY = 2;
  const MAX_RESULTS = 36;
  const MAX_PREDICTIVE = 10;
  const INDEX_CACHE_KEY = 'frau_sku_search_index_v3';
  const INDEX_CACHE_TTL_MS = 1000 * 60 * 30;

  let indexPromise = null;

  /** @type {{ handle: string, title: string, skus: string[] }[]} */
  let skuIndex = [];

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  async function mapPool(items, concurrency, mapper) {
    if (!items.length) return [];
    const results = new Array(items.length);
    let nextIndex = 0;
    const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
      while (nextIndex < items.length) {
        const i = nextIndex++;
        results[i] = await mapper(items[i], i);
      }
    });
    await Promise.all(workers);
    return results;
  }

  function shopRoot() {
    const root = window.theme?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  /** Section Rendering API sul tema in anteprima (senza questo le sezioni SKU sono null sul tema live). */
  function getPreviewThemeId() {
    const fromConfig = window.theme?.skuPartialSearch?.previewThemeId;
    if (fromConfig != null && fromConfig !== '') return String(fromConfig);
    try {
      return new URLSearchParams(window.location.search).get('preview_theme_id');
    } catch {
      return null;
    }
  }

  function appendPreviewThemeToUrl(url) {
    const previewId = getPreviewThemeId();
    if (!previewId) return url;
    const sep = url.includes('?') ? '&' : '?';
    if (url.includes('preview_theme_id=')) return url;
    return `${url}${sep}preview_theme_id=${encodeURIComponent(previewId)}`;
  }

  /** Come il tema Broadcast: /products/{handle}?section_id=api-* → HTML + [data-api-content]. */
  async function fetchThemeApiSectionInnerHtml(productHandle, sectionId) {
    const url = appendPreviewThemeToUrl(
      `${shopRoot()}products/${encodeURIComponent(productHandle)}?section_id=${encodeURIComponent(sectionId)}`
    );
    const res = await fetch(url);
    if (!res.ok) return '';
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const apiContent = doc.querySelector('[data-api-content]');
    if (apiContent?.innerHTML?.trim()) return apiContent.innerHTML.trim();
    const sectionEl = doc.querySelector(`#shopify-section-${sectionId}`);
    return sectionEl?.innerHTML?.trim() || '';
  }

  function normalizeQuery(raw) {
    return raw.trim().replace(/\s+/g, '');
  }

  /** Two or more digits only (client requirement: "65", "76"). */
  function isDigitFragmentQuery(q) {
    return /^\d{2,}$/.test(normalizeQuery(q));
  }

  /**
   * True only for SKU-style queries. Normal text search (titles, multi-word) is unchanged.
   */
  function usesSkuIndex(q) {
    const raw = q.trim();
    if (raw.length < MIN_DIGIT_QUERY) return false;
    if (/\s/.test(raw)) return false;

    const c = normalizeQuery(raw);
    if (isDigitFragmentQuery(c)) return true;
    if (c.includes('_')) return true;
    if (/^[a-z]\d{2}[a-z0-9]*$/i.test(c)) return true;
    if (/^[a-z]{2}\d{4}$/i.test(c)) return false;
    if (/^[a-z]{2}\d{2,}[a-z0-9]*$/i.test(c) && c.length <= 12) return true;
    if (/^b\d[a-z0-9_]*$/i.test(c)) return true;
    if (/^\d+[a-z_]/i.test(c)) return true;
    return false;
  }

  function getPredictiveSearchRoot(form) {
    if (!form) return null;
    return form.closest('predictive-search') || form.querySelector('predictive-search');
  }

  /** Il pannello risultati sta fuori dal <form> (header popdown Broadcast). */
  function getPredictiveResultsHost(form) {
    const root = getPredictiveSearchRoot(form);
    return (
      root?.querySelector('[data-predictive-search-results]') ||
      form?.querySelector('[data-predictive-search-results]')
    );
  }

  function isSkuPartialRendered(host, query) {
    return (
      host?.dataset.skuPartialActive === 'true' &&
      host.dataset.skuPartialQuery === query &&
      Boolean(host.querySelector('[data-sku-partial-results], [data-sku-partial-search]'))
    );
  }

  /** Blocca predictive nativa e onChange che rimuove [data-search-results-groups-wrapper] subito dopo il nostro inject. */
  function patchPredictiveSearchInstance(form) {
    const predictiveSearch = getPredictiveSearchRoot(form);
    if (!predictiveSearch || predictiveSearch.dataset.skuPartialPatched === 'true') return;

    const predictiveProto = Object.getPrototypeOf(predictiveSearch);
    const headerProto = Object.getPrototypeOf(predictiveProto);
    const nativeOnChange =
      typeof predictiveProto?.onChange === 'function' ? predictiveProto.onChange : null;
    const nativeGetSearchResults =
      typeof predictiveSearch.getSearchResults === 'function'
        ? predictiveSearch.getSearchResults.bind(predictiveSearch)
        : null;

    predictiveSearch.dataset.skuPartialPatched = 'true';

    if (nativeGetSearchResults) {
      predictiveSearch.getSearchResults = function (searchTerm) {
        if (usesSkuIndex(searchTerm)) {
          this.abortController?.abort();
          this.abortController = new AbortController();
          this.removeAttribute('loading');
          return;
        }
        return nativeGetSearchResults(searchTerm);
      };
    }

    if (nativeOnChange) {
      predictiveSearch.onChange = function () {
        if (typeof headerProto?.onChange === 'function') {
          headerProto.onChange.call(this);
        }

        const q = this.getQuery();
        if (usesSkuIndex(q)) {
          this.searchTerm = q;
          if (!q.length) {
            if (typeof this.reset === 'function') this.reset();
            releasePredictiveToTheme(form);
            return;
          }
          if (this.input) showPredictiveSkuResults(this.input);
          return;
        }

        return nativeOnChange.call(this);
      };
    }
  }

  function restoreNativePredictiveShell(host) {
    if (!host) return;
    host.innerHTML = `
      <div class="predictive-search__loading-state">
        <div class="predictive-search__loader loader"><div class="loader-indeterminate"></div></div>
      </div>`;
    host.classList.add('predictive-search--empty');
    delete host.dataset.skuPartialActive;
    delete host.dataset.skuPartialQuery;
  }

  function releasePredictiveToTheme(form) {
    const host = getPredictiveResultsHost(form);
    if (!host || host.dataset.skuPartialActive !== 'true') return;

    restoreNativePredictiveShell(host);

    const input = form.querySelector('input[name="q"]');
    if (input?.value.trim()) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  function scoreSkuMatch(sku, q) {
    const i = sku.indexOf(q);
    if (i === -1) return Number.MAX_SAFE_INTEGER;
    return i * 100 + sku.length;
  }

  /** Codice modello/colore in coda all’handle (es. …-a76s7_brown → a76s7_brown). */
  function extractHandleSkuKey(handle) {
    if (!handle) return '';
    const part = handle.split('-').pop() || '';
    if (/^[a-z]\d[a-z0-9_]*$/i.test(part)) return part.toLowerCase();
    return '';
  }

  function pickDisplaySku(entry, handleKey) {
    if (!entry.skus?.length) return handleKey;
    const prefix = handleKey.replace(/_\d+$/, '');
    const byPrefix = entry.skus.find((s) => s.startsWith(prefix) || s.includes(handleKey));
    return byPrefix || entry.skus[0];
  }

  function buildIndexFromProducts(products) {
    const entries = [];
    for (const product of products) {
      const skus = (product.variants || [])
        .map((v) => (v.sku || '').trim())
        .filter(Boolean)
        .map((s) => s.toLowerCase());
      const handleKey = extractHandleSkuKey(product.handle);
      if (!skus.length && !handleKey) continue;
      entries.push({
        handle: product.handle,
        title: product.title,
        skus: [...new Set(skus)],
        handleKey,
      });
    }
    return entries;
  }

  function readCachedIndex() {
    try {
      const raw = sessionStorage.getItem(INDEX_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.entries || !parsed?.ts) return null;
      if (Date.now() - parsed.ts > INDEX_CACHE_TTL_MS) return null;
      return parsed.entries;
    } catch {
      return null;
    }
  }

  function writeCachedIndex(entries) {
    try {
      sessionStorage.setItem(INDEX_CACHE_KEY, JSON.stringify({ ts: Date.now(), entries }));
    } catch {
      /* quota */
    }
  }

  async function fetchAllProducts() {
    const all = [];
    for (let page = 1; page <= 80; page++) {
      const res = await fetch(`${shopRoot()}collections/all/products.json?limit=250&page=${page}`);
      if (!res.ok) break;
      const data = await res.json();
      const batch = data?.products || [];
      all.push(...batch);
      if (batch.length < 250) break;
    }
    return all;
  }

  async function ensureIndexLoaded() {
    if (skuIndex.length) return skuIndex;
    if (indexPromise) return indexPromise;

    indexPromise = (async () => {
      const cached = readCachedIndex();
      if (cached?.length) {
        skuIndex = cached;
        return skuIndex;
      }
      const products = await fetchAllProducts();
      skuIndex = buildIndexFromProducts(products);
      writeCachedIndex(skuIndex);
      return skuIndex;
    })();

    return indexPromise;
  }

  function findSkuMatches(query) {
    const q = normalizeQuery(query).toLowerCase();
    if (!q) return [];

    const results = [];
    const seen = new Set();

    for (const entry of skuIndex) {
      let bestSku = null;
      let bestScore = Number.MAX_SAFE_INTEGER;

      for (const sku of entry.skus) {
        if (!sku.includes(q)) continue;
        const score = scoreSkuMatch(sku, q);
        if (score < bestScore) {
          bestScore = score;
          bestSku = sku;
        }
      }

      if (entry.handleKey && entry.handleKey.includes(q)) {
        const score = scoreSkuMatch(entry.handleKey, q) + 0.5;
        if (score < bestScore) {
          bestScore = score;
          bestSku = pickDisplaySku(entry, entry.handleKey);
        }
      }

      if (bestSku == null || seen.has(entry.handle)) continue;
      seen.add(entry.handle);
      results.push({
        handle: entry.handle,
        title: entry.title,
        matchedSku: bestSku,
        score: bestScore,
      });
    }

    return results.sort((a, b) => a.score - b.score);
  }

  function productPath(handle) {
    return `${shopRoot()}products/${handle}`;
  }

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
  }

  const PREDICTIVE_ICON_ARROW =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-nav-arrow-right" viewBox="0 0 24 24"><path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /** Theme images injected after load stay on is-loading (opacity ~0) unless cleared. */
  function stripLoadingClass(node) {
    if (!node || !node.classList?.contains('is-loading')) return;
    node.classList.remove('is-loading');
  }

  function revealInjectedImages(container) {
    if (!container) return;

    container.querySelectorAll('[data-aos]').forEach((el) => {
      el.classList.add('aos-animate');
    });

    container.querySelectorAll('img').forEach((img) => {
      const reveal = () => {
        stripLoadingClass(img);
        let parent = img.parentElement;
        for (let i = 0; i < 4 && parent; i++) {
          stripLoadingClass(parent);
          parent = parent.parentElement;
        }
      };

      if (img.complete) {
        reveal();
      } else {
        img.addEventListener('load', reveal, { once: true });
        img.addEventListener('error', reveal, { once: true });
      }
    });
  }

  async function fetchNativePredictiveCardHtml(handle) {
    const sectionId =
      window.theme?.skuPartialSearch?.predictiveCardSectionId || 'api-search-product-item';
    const inner = await fetchThemeApiSectionInnerHtml(handle, sectionId);
    if (!inner) return '';
    const probe = new DOMParser().parseFromString(inner, 'text/html');
    const card = probe.querySelector('.predictive-search__grid-item');
    return card ? card.outerHTML : inner;
  }

  async function fetchNativeSearchGridItemHtml(handle) {
    const sectionId =
      window.theme?.skuPartialSearch?.searchGridSectionId || 'api-product-grid-item';
    const inner = await fetchThemeApiSectionInnerHtml(handle, sectionId);
    if (!inner) return '';
    const probe = new DOMParser().parseFromString(inner, 'text/html');
    const el = probe.querySelector('grid-item') || probe.querySelector('.grid-item.product-item');
    return el ? el.outerHTML : inner;
  }

  function tagPredictiveCardHtml(html) {
    if (!html || html.includes('data-sku-partial-search')) return html;
    return html.replace(
      /class="predictive-search__grid-item/,
      'data-sku-partial-search class="predictive-search__grid-item'
    );
  }

  async function buildSearchGridHtml(matches) {
    const handles = matches.map((m) => m.handle);
    const parts = await mapPool(handles, 8, (h) => fetchNativeSearchGridItemHtml(h));
    const html = parts.filter(Boolean).join('');
    return html || null;
  }

  /** Evita renderSearchResults() del tema: setLiveRegionResults può lanciare e lasciare il pannello chiuso. */
  function applyPredictiveMarkup(form, markup) {
    const resultsHost = getPredictiveResultsHost(form);
    const predictiveSearch = getPredictiveSearchRoot(form);
    if (!resultsHost || !markup) return false;

    resultsHost.innerHTML = markup;
    resultsHost.classList.remove('predictive-search--empty');

    if (predictiveSearch) {
      predictiveSearch.removeAttribute('loading');
      predictiveSearch.setAttribute('results', 'true');
      predictiveSearch.setAttribute('open', 'true');

      const searchInput = predictiveSearch.querySelector('input[type="search"]');
      searchInput?.setAttribute('aria-expanded', 'true');

      const live = resultsHost.querySelector('[data-predictive-search-live-region-count-value]');
      const status = predictiveSearch.querySelector('[data-predictive-search-status]');
      if (live?.textContent && status) {
        status.textContent = live.textContent;
        status.setAttribute('aria-hidden', 'false');
        setTimeout(() => status.setAttribute('aria-hidden', 'true'), 1000);
      }

      try {
        const top = resultsHost.getBoundingClientRect().top;
        const vh = window.visualViewport?.height ?? window.innerHeight;
        resultsHost.style.setProperty('--results-height', `${vh - top}px`);
      } catch {
        /* ok */
      }
    }

    openPredictiveSearch(form);
    return true;
  }

  function reinitProductGridCards(container) {
    if (!container) return;
    revealInjectedImages(container);
    requestAnimationFrame(() => {
      container.querySelectorAll('hover-images').forEach((el) => {
        if (el.flkty) el.flkty.resize();
      });
    });
  }

  /** Card da api-search-product-item (snippet search-product-item, come mocassini). */
  async function buildFallbackPredictiveMarkup(matches, query) {
    const htmlParts = (
      await mapPool(matches, 8, (m) => fetchNativePredictiveCardHtml(m.handle))
    )
      .map(tagPredictiveCardHtml)
      .filter(Boolean);
    if (!htmlParts.length) return null;
    return renderPredictiveLayoutFallback(htmlParts.join(''), query, htmlParts.length);
  }

  function formatSearchForLabel(query) {
    const template = window.theme?.strings?.searchFor || 'Search for "{{ terms }}"';
    return escapeHtml(template.replace('__TERMS__', query).replace(/\{\{\s*terms\s*\}\}/gi, query));
  }

  function decodeThemeTranslation(text) {
    if (!text || !text.includes('&')) return text;
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  }

  function formatSearchPageCaption(count, term) {
    const forms = window.theme?.strings?.searchResultsWithCountAndSkuTerm;
    const raw =
      (count === 1 ? forms?.one : forms?.other) ||
      (count === 1
        ? '__COUNT__ risultato trovato per SKU "__TERMS__"'
        : '__COUNT__ risultati trovati per SKU "__TERMS__"');
    const template = decodeThemeTranslation(raw);
    return template
      .replace(/__COUNT__/g, String(count))
      .replace(/\{\{\s*count\s*\}\}/g, String(count))
      .replace(/__TERMS__/g, term)
      .replace(/\{\{\s*terms\s*\}\}/gi, term);
  }

  /** Layout predictive nativo (card da ?section_id=api-search-product-item). */
  function renderPredictiveLayoutFallback(productsHtml, query, productCount) {
    const heading = escapeHtml(window.theme?.strings?.searchProducts || 'Products');
    const searchForLabel = formatSearchForLabel(query);
    const liveText = escapeHtml(
      `${productCount} ${productCount === 1 ? 'product' : 'products'}: ${query}`
    );

    return `
      <div class="wrapper">
        <div id="predictive-search-results" role="listbox">
          <div
            class="predictive-search__layout predictive-search__layout--no-suggestions"
            data-sku-partial-results
          >
            <div class="predictive-search__column">
              <div
                class="predictive-search__group"
                data-aos="fade"
                data-aos-delay="100"
                data-aos-duration="800"
                data-aos-anchor="#predictive-search-results"
              >
                <p class="predictive-search__heading" id="predictive-search-products">${heading}</p>
                <div
                  class="predictive-search__products__list grid-outer"
                  role="listbox"
                  aria-labelledby="predictive-search-products"
                  data-search-results
                >
                  <div class="grid">${productsHtml}</div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="predictive-search__actions"
            data-aos="fade"
            data-aos-delay="200"
            data-aos-duration="800"
            data-aos-anchor="#predictive-search-results"
          >
            <button class="btn btn--outline btn--primary" tabindex="-1" role="option" aria-selected="false" type="submit">
              <span data-predictive-search-search-for-text>${searchForLabel}</span>
              ${PREDICTIVE_ICON_ARROW}
            </button>
          </div>
          <div class="predictive-search__loading-state">
            <div class="predictive-search__loader loader"><div class="loader-indeterminate"></div></div>
          </div>
          <span class="hidden" data-predictive-search-live-region-count-value aria-live="polite">${liveText}</span>
        </div>
      </div>`;
  }

  async function renderMatchesIntoGrid(loop, matches) {
    const slice = matches.slice(0, MAX_RESULTS);
    const html = await buildSearchGridHtml(slice);
    if (!html) return 0;
    loop.innerHTML = html;
    reinitProductGridCards(loop);
    return slice.length;
  }

  function updateSearchPageCaption(count, term) {
    const text = formatSearchPageCaption(count, term);
    document.querySelectorAll('.search-page .search__caption').forEach((caption) => {
      caption.textContent = text;
      caption.hidden = false;
    });
  }

  function shopifySearchHasProducts(loop) {
    if (!loop) return false;
    if (loop.querySelector('.no-results')) return false;
    return Boolean(loop.querySelector('.product-item, .grid-item'));
  }

  async function enhanceSearchPage() {
    const page = document.querySelector('.search-page');
    if (!page) return;

    const performed = page.dataset.searchPerformed === 'true';
    const params = new URLSearchParams(window.location.search);
    const q = (params.get('q') || page.querySelector('input[name="q"]')?.value || '').trim();
    if (!q || !usesSkuIndex(q)) return;
    if (!performed && !params.get('q')) return;

    const loop = document.getElementById('SearchLoop');
    if (!loop) return;

    if (!isDigitFragmentQuery(q) && shopifySearchHasProducts(loop)) return;

    if (isDigitFragmentQuery(q)) {
      loop.querySelector('.no-results')?.remove();
      document.querySelectorAll('.search-page .search__caption').forEach((caption) => {
        caption.hidden = true;
      });
      loop.closest('[data-products-grid]')?.classList.add('is-loading');
    }

    await ensureIndexLoaded();
    const matches = findSkuMatches(q);
    const gridOuter = loop.closest('[data-products-grid]');

    if (!matches.length) {
      gridOuter?.classList.remove('is-loading');
      return;
    }

    loop.querySelector('.no-results')?.remove();
    const count = await renderMatchesIntoGrid(loop, matches);
    if (count > 0) {
      updateSearchPageCaption(count, q);
    }

    gridOuter?.classList.remove('is-loading');
    gridOuter?.querySelector('.grid__loader')?.remove();
  }

  function clearSkuPredictiveOverride(form) {
    form.querySelectorAll('[data-sku-partial-results]').forEach((el) => el.remove());
    form.querySelectorAll('[data-sku-partial-search]').forEach((el) => el.remove());
  }

  function openPredictiveSearch(form) {
    const popdown = form.closest('[data-popdown]');
    popdown?.classList.add('is-searched');
    const outer = popdown?.querySelector('.predictive-search__outer');
    outer?.classList.add('predictive-search__outer--empty', 'is-searched');

    const predictiveSearch = getPredictiveSearchRoot(form);
    if (predictiveSearch) {
      predictiveSearch.setAttribute('open', 'true');
      predictiveSearch.setAttribute('results', 'true');
      predictiveSearch.removeAttribute('loading');
    }
  }

  let suppressPredictiveObserver = false;
  let skuPredictiveBusy = false;

  async function showPredictiveSkuResults(input) {
    if (skuPredictiveBusy) return;
    const q = input.value.trim();
    const form = input.closest('form.search-form');
    if (!form) return;

    const resultsHost = getPredictiveResultsHost(form);
    if (!resultsHost) return;

    if (!usesSkuIndex(q)) {
      releasePredictiveToTheme(form);
      clearSkuPredictiveOverride(form);
      return;
    }

    patchPredictiveSearchInstance(form);

    if (isSkuPartialRendered(resultsHost, q)) return;

    skuPredictiveBusy = true;
    await ensureIndexLoaded();
    const matches = findSkuMatches(q).slice(0, MAX_PREDICTIVE);

    if (!matches.length) {
      releasePredictiveToTheme(form);
      clearSkuPredictiveOverride(form);
      skuPredictiveBusy = false;
      return;
    }

    let markup = await buildFallbackPredictiveMarkup(matches, q);
    if (!markup) {
      releasePredictiveToTheme(form);
      clearSkuPredictiveOverride(form);
      skuPredictiveBusy = false;
      return;
    }

    suppressPredictiveObserver = true;
    applyPredictiveMarkup(form, markup);

    resultsHost.dataset.skuPartialActive = 'true';
    resultsHost.dataset.skuPartialQuery = q;
    reinitProductGridCards(resultsHost);

    requestAnimationFrame(() => {
      reinitProductGridCards(resultsHost);
      suppressPredictiveObserver = false;
      skuPredictiveBusy = false;
    });
  }

  function attachPredictiveGuard(input) {
    const form = input.closest('form.search-form');
    const host = getPredictiveResultsHost(form);
    if (!host || host.dataset.skuPartialGuard) return;
    host.dataset.skuPartialGuard = 'true';

    const rerun = debounce(() => {
      if (suppressPredictiveObserver) return;
      const q = input.value.trim();
      if (!usesSkuIndex(q)) return;
      if (isSkuPartialRendered(host, q)) return;
      showPredictiveSkuResults(input);
    }, 150);

    new MutationObserver(rerun).observe(host, { childList: true, subtree: true });
  }

  async function onSearchSubmit(event) {
    const form = event.target;
    if (!form.matches('form.search-form')) return;

    const input = form.querySelector('input[name="q"]');
    const q = input?.value?.trim();
    if (!q || !usesSkuIndex(q)) return;

    if (isDigitFragmentQuery(q)) return;

    await ensureIndexLoaded();
    const matches = findSkuMatches(q);
    if (matches.length === 1) {
      event.preventDefault();
      window.location.href = productPath(matches[0].handle);
    }
  }

  function preloadIndex() {
    ensureIndexLoaded().catch(() => {});
  }

  function init() {
    document.querySelectorAll('form.search-form').forEach((form) => {
      try {
        patchPredictiveSearchInstance(form);
      } catch {
        /* ok */
      }
      form.addEventListener('submit', onSearchSubmit);
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      if (getPredictiveResultsHost(form)) {
        input.addEventListener('focus', preloadIndex, { once: true });
        attachPredictiveGuard(input);
      }
    });

    enhanceSearchPage();
    preloadIndex();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
